import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/useAuth';
import {
  LiksMembersPanel,
  type LiksMembersPanelMember,
} from '../../../components/liks/LiksMembersPanel/LiksMembersPanel';
import {
  canLeaveKdb,
  canManageKdbMembers,
  canManageKdbMemberTarget,
} from '../../../features/knowledge/knowledgeDatabasePermissions';
import {
  useInviteKnowledgeMemberMutation,
  useRemoveKnowledgeMemberMutation,
  useUpdateKnowledgeMemberRoleMutation,
} from '../../../features/knowledge/mutations/useKnowledgeMemberMutations';
import { useKnowledgeGroupAssignmentsQuery } from '../../../features/knowledge/queries/useKnowledgeGroupAssignmentsQuery';
import { useTenantGroupMembersQueries } from '../../../features/tenant/queries/useTenantGroupMembersQuery';
import { useTenantUsersQuery } from '../../../features/tenant/queries/useTenantUsersQuery';
import { formatDirectoryUserDisplayName } from '../../../features/user/userDisplay';
import { useI18n } from '../../../i18n/useI18n';
import { resolveApiErrorMessage } from '../../../query/apiErrorMessage';
import { toastMutationError } from '../../../query/mutationToasts';
import { handlePossibleTenantDeactivatedError } from '../../../query/tenantAccessBlocked';
import { toast } from '../../../toast/toast';
import type {
  KnowledgeBaseMembership,
  KnowledgeMemberRole,
} from '../../../types/knowledge';
import { KnowledgeDatabaseGroupAssignments } from '../KnowledgeDatabaseGroupAssignments';
import {
  buildKnowledgeMembersPanelLabels,
  KNOWLEDGE_MEMBER_ROLES,
  toKnowledgePanelMember,
} from './knowledgeDatabaseMembersPanelHelpers';
import { buildGroupOnlyKnowledgeMembers } from './buildGroupOnlyKnowledgeMembers';
import '../knowledgeDatabaseShared.css';

type KnowledgeDatabaseMembersPanelProps = {
  databaseId: string;
  databaseName: string;
  members: KnowledgeBaseMembership[];
};

export function KnowledgeDatabaseMembersPanel({
  databaseId,
  databaseName,
  members,
}: KnowledgeDatabaseMembersPanelProps) {
  const { translate } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const inviteMemberMutation = useInviteKnowledgeMemberMutation();
  const updateMemberRoleMutation = useUpdateKnowledgeMemberRoleMutation();
  const removeMemberMutation = useRemoveKnowledgeMemberMutation();
  const directoryUsersQuery = useTenantUsersQuery({ enabled: true });
  const groupAssignmentsQuery = useKnowledgeGroupAssignmentsQuery(databaseId);

  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);

  const assignedGroupIds = useMemo(
    () =>
      (groupAssignmentsQuery.data ?? [])
        .map((assignment) => assignment.userGroupId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    [groupAssignmentsQuery.data],
  );

  const groupMemberQueries = useTenantGroupMembersQueries(assignedGroupIds);

  const groupOnlyMembers = useMemo(
    () =>
      buildGroupOnlyKnowledgeMembers({
        assignments: groupAssignmentsQuery.data ?? [],
        groupMemberLists: groupMemberQueries.map((query) => query.data),
        members,
      }),
    [groupAssignmentsQuery.data, groupMemberQueries, members],
  );

  const groupOnlyUserIds = useMemo(
    () => new Set(groupOnlyMembers.map((member) => member.userId)),
    [groupOnlyMembers],
  );

  const panelMembers = useMemo(() => {
    const direct = members
      .map(toKnowledgePanelMember)
      .filter(
        (member): member is LiksMembersPanelMember<KnowledgeMemberRole> =>
          member !== null,
      );
    return [...direct, ...groupOnlyMembers];
  }, [groupOnlyMembers, members]);

  const membersByUserId = useMemo(() => {
    const map = new Map<string, KnowledgeBaseMembership>();
    for (const member of members) {
      if (member.userId) {
        map.set(member.userId, member);
      }
    }
    return map;
  }, [members]);

  const currentMember = user?.id ? membersByUserId.get(user.id) : undefined;
  const currentMemberRole = currentMember?.role ?? null;
  const canManage = canManageKdbMembers(currentMemberRole);

  const roleOptions = useMemo(
    () =>
      KNOWLEDGE_MEMBER_ROLES.map((role) => ({
        value: role,
        label: translate(`knowledge.members.roles.${role}`),
      })),
    [translate],
  );

  const labels = useMemo(
    () => buildKnowledgeMembersPanelLabels(translate),
    [translate],
  );

  const handleInvite = (userId: string, role: KnowledgeMemberRole) => {
    setPendingUserId(userId);
    inviteMemberMutation.mutate(
      {
        databaseId,
        userId,
        role,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.members.inviteSuccessTitle'),
            description: '',
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.members.inviteErrorTitle'),
          );
        },
        onSettled: () => {
          setPendingUserId(null);
        },
      },
    );
  };

  const handleRoleChange = (
    panelMember: LiksMembersPanelMember<KnowledgeMemberRole>,
    role: KnowledgeMemberRole,
  ) => {
    const member = membersByUserId.get(panelMember.userId);
    if (!member || member.role === role) {
      return;
    }

    const username = formatDirectoryUserDisplayName(
      directoryUsersQuery.data?.find(
        (entry) => entry.id === panelMember.userId,
      ),
      labels.unknownUser,
    );

    setPendingUserId(panelMember.userId);
    updateMemberRoleMutation.mutate(
      {
        databaseId,
        member,
        role,
      },
      {
        onError: (error) => {
          void (async () => {
            if (await handlePossibleTenantDeactivatedError(error)) {
              return;
            }
            const apiMessage = await resolveApiErrorMessage(error);
            toast.error({
              title: translate('knowledge.members.roleErrorTitle', {
                username,
              }),
              description: translate('knowledge.members.roleErrorDescription', {
                error:
                  apiMessage !== ''
                    ? apiMessage
                    : translate('knowledge.deleteErrorDescription'),
              }),
            });
          })();
        },
        onSettled: () => {
          setPendingUserId(null);
        },
      },
    );
  };

  const handleRemove = (
    panelMember: LiksMembersPanelMember<KnowledgeMemberRole>,
  ) => {
    if (groupOnlyUserIds.has(panelMember.userId) || pendingUserId != null) {
      return;
    }

    setPendingUserId(panelMember.userId);
    removeMemberMutation.mutate(
      {
        databaseId,
        memberId: panelMember.id,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.members.removeMemberSuccessTitle'),
            description: '',
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.deleteErrorTitle'),
            translate('knowledge.deleteErrorDescription'),
          );
        },
        onSettled: () => {
          setPendingUserId(null);
        },
      },
    );
  };

  const handleLeave = () => {
    if (!currentMember?.id) {
      return;
    }

    setIsLeaving(true);
    removeMemberMutation.mutate(
      {
        databaseId,
        memberId: currentMember.id,
      },
      {
        onSuccess: () => {
          navigate('/knowledge');
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.members.leaveErrorTitle'),
          );
        },
        onSettled: () => {
          setIsLeaving(false);
        },
      },
    );
  };

  return (
    <div className="knowledge-database__panel">
      <KnowledgeDatabaseGroupAssignments
        databaseId={databaseId}
        canManage={canManage}
      />
      <LiksMembersPanel
        resourceName={databaseName}
        members={panelMembers}
        directoryUsers={directoryUsersQuery.data ?? []}
        isLoadingDirectoryUsers={directoryUsersQuery.isLoading}
        directoryUsersError={directoryUsersQuery.isError}
        roleOptions={roleOptions}
        defaultInviteRole="VIEWER"
        currentUserId={user?.id}
        canManageMembers={canManage}
        canLeave={canLeaveKdb(currentMemberRole)}
        canManageMember={(member) =>
          !groupOnlyUserIds.has(member.userId) &&
          canManageKdbMemberTarget(currentMemberRole, member.userId, user?.id)
        }
        adminRole="ADMIN"
        labels={labels}
        pendingUserId={pendingUserId}
        isLeaving={isLeaving}
        onInvite={handleInvite}
        onRoleChange={handleRoleChange}
        onRemove={handleRemove}
        onLeave={handleLeave}
      />
    </div>
  );
}
